import { useQuery } from "@tanstack/react-query";
import { CalendarRange, LogOut, Map, Plus, Sparkles, Users } from "lucide-react";
import { useEffect, useState } from "react";
import type { TripDTO } from "@shared/types";
import { daysBetween } from "@shared/types";
import { get, post } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { countdown, fmtDay, fmtRange, timeIn, weekday } from "@/lib/format";
import { CATEGORY, ITEM_KIND, MODE } from "@/lib/meta";
import { toast } from "@/lib/store";
import { useDerived, useTripBundle } from "@/lib/trip";
import { Button, Field, Input, Modal, Spinner } from "./ui";

function Logo({ size = 40 }: { size?: number }) {
  return (
    <div className="grid place-items-center rounded-2xl bg-gradient-to-br from-jade-500 to-jade-800 text-white shadow-lg" style={{ width: size, height: size }}>
      <Map size={size * 0.5} />
    </div>
  );
}

export function LoginScreen({ invite }: { invite?: { tripTitle: string; startDate: string; endDate: string } | null }) {
  const { config, signInGoogle, signInDev } = useAuth();
  const [email, setEmail] = useState("");
  return (
    <div className="relative grid min-h-dvh place-items-center overflow-hidden p-6">
      <div className="pointer-events-none absolute inset-0 opacity-60" style={{ background: "radial-gradient(60% 50% at 20% 10%, #aee4d6 0%, transparent 60%), radial-gradient(50% 50% at 90% 90%, #f9c9a7 0%, transparent 60%)" }} />
      <div className="glass relative w-full max-w-md rounded-[28px] p-7 text-center">
        <div className="mb-5 flex justify-center">
          <Logo size={56} />
        </div>
        <h1 className="font-display text-[26px] font-bold">Tripboard</h1>
        <p className="mt-1 mb-6 text-[15px] text-muted">Карта, план по дням, идеи друзей и ИИ-ассистент — для путешествий вместе.</p>
        {invite && (
          <div className="mb-5 rounded-2xl bg-[var(--accent-soft)] p-3 text-[14px]">
            Вас пригласили в поездку <b>«{invite.tripTitle}»</b>
            <div className="text-[12px] text-muted">{fmtRange(invite.startDate, invite.endDate)}</div>
          </div>
        )}
        <Button variant="primary" className="h-12 w-full text-[15px]" onClick={signInGoogle} disabled={!config?.supabaseKey}>
          <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden>
            <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
            <path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
            <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
            <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
          </svg>
          Войти через Google
        </Button>
        {config?.devLogin && (
          <div className="mt-5 border-t border-[var(--border)] pt-4 text-left">
            <Field label="Тестовый вход (только разработка)">
              <div className="flex gap-2">
                <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@test.lt" onKeyDown={(e) => e.key === "Enter" && signInDev(email)} />
                <Button onClick={() => signInDev(email)}>Войти</Button>
              </div>
            </Field>
          </div>
        )}
      </div>
    </div>
  );
}

function NewTripDialog({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const [v, setV] = useState({ title: "", startDate: "", endDate: "", originName: "Вильнюс" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!v.title || !v.startDate || !v.endDate) return toast("Название и даты", { tone: "error" });
    setBusy(true);
    try {
      const t = await post<TripDTO>("/api/trips", { ...v, originLat: 54.6872, originLng: 25.2797, settings: {} });
      onCreated(t.id);
    } catch (e) {
      toast((e as Error).message, { tone: "error" });
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      open
      onClose={onClose}
      title="Новая поездка"
      footer={
        <>
          <Button onClick={onClose}>Отмена</Button>
          <Button variant="primary" loading={busy} onClick={save}>
            Создать
          </Button>
        </>
      }
    >
      <div className="grid gap-3">
        <Field label="Название">
          <Input autoFocus value={v.title} onChange={(e) => setV({ ...v, title: e.target.value })} placeholder="Япония весной" />
        </Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label="С">
            <Input type="date" value={v.startDate} onChange={(e) => setV({ ...v, startDate: e.target.value })} />
          </Field>
          <Field label="По">
            <Input type="date" value={v.endDate} onChange={(e) => setV({ ...v, endDate: e.target.value })} />
          </Field>
        </div>
        <Field label="Откуда летим">
          <Input value={v.originName} onChange={(e) => setV({ ...v, originName: e.target.value })} />
        </Field>
      </div>
    </Modal>
  );
}

export function TripsScreen({ onOpen }: { onOpen: (id: string) => void }) {
  const { user, signOut } = useAuth();
  const q = useQuery({ queryKey: ["trips"], queryFn: () => get<TripDTO[]>("/api/trips") });
  const [creating, setCreating] = useState(false);
  const [templBusy, setTemplBusy] = useState(false);

  const fromTemplate = async () => {
    setTemplBusy(true);
    try {
      const r = await post<{ id: string }>("/api/trips/from-template", { template: "vietnam-2027" });
      onOpen(r.id);
    } catch (e) {
      toast((e as Error).message, { tone: "error" });
    } finally {
      setTemplBusy(false);
    }
  };

  return (
    <div className="mx-auto min-h-dvh max-w-3xl px-4 py-8 sm:py-12">
      <header className="mb-8 flex items-center gap-3">
        <Logo />
        <div className="flex-1">
          <h1 className="font-display text-[22px] font-bold">Tripboard</h1>
          <div className="text-[13px] text-muted">Привет, {user?.name?.split(" ")[0] ?? user?.email}</div>
        </div>
        <Button variant="ghost" icon={<LogOut size={16} />} onClick={signOut}>
          Выйти
        </Button>
      </header>

      {q.isLoading && <Spinner className="mx-auto text-[var(--accent)]" />}
      <div className="grid gap-3 sm:grid-cols-2">
        {q.data?.map((t) => {
          const cd = countdown(t.startDate, t.endDate);
          return (
            <button key={t.id} onClick={() => onOpen(t.id)} className="group surface relative overflow-hidden rounded-3xl p-5 text-left transition hover:-translate-y-0.5 hover:shadow-[var(--shadow)]">
              <div className="pointer-events-none absolute -top-10 -right-10 size-40 rounded-full bg-gradient-to-br from-jade-200 to-transparent opacity-60 dark:from-jade-800" />
              <div className="relative">
                <div className="mb-3 inline-flex items-center gap-1.5 rounded-full bg-[var(--accent-soft)] px-2.5 py-1 text-[12px] font-bold text-[var(--accent)]">
                  <CalendarRange size={13} /> {cd.text}
                </div>
                <div className="font-display text-[19px] leading-tight font-bold">{t.title}</div>
                {t.subtitle && <div className="mt-0.5 text-[13px] text-muted">{t.subtitle}</div>}
                <div className="mt-3 flex items-center gap-3 text-[12px] text-muted">
                  <span>{fmtRange(t.startDate, t.endDate)}</span>
                  <span>· {daysBetween(t.startDate, t.endDate) + 1} дн.</span>
                  {t.myRole !== "owner" && (
                    <span className="flex items-center gap-1">
                      <Users size={12} /> приглашение
                    </span>
                  )}
                </div>
              </div>
            </button>
          );
        })}
        <button onClick={() => setCreating(true)} className="flex min-h-36 flex-col items-center justify-center gap-2 rounded-3xl border-2 border-dashed border-[var(--border-strong)] p-5 text-muted transition hover:border-[var(--accent)] hover:text-[var(--accent)]">
          <Plus size={24} />
          <span className="font-bold">Новая поездка</span>
        </button>
      </div>

      {!q.data?.some((t) => t.startDate === "2027-04-06") && (
        <div className="surface mt-6 flex flex-col items-start gap-3 rounded-3xl p-5 sm:flex-row sm:items-center">
          <div className="text-3xl">🇻🇳</div>
          <div className="flex-1">
            <div className="font-bold">Вьетнам · 6–28 апреля 2027 · из Вильнюса</div>
            <div className="text-[13px] text-muted">Черновик маршрута север → юг: 54 места, план на 23 дня, переезды и чек-лист. Можно сразу менять или попросить ИИ перестроить.</div>
          </div>
          <Button variant="primary" loading={templBusy} icon={<Sparkles size={16} />} onClick={fromTemplate}>
            Создать из шаблона
          </Button>
        </div>
      )}
      {creating && (
        <NewTripDialog
          onClose={() => setCreating(false)}
          onCreated={(id) => {
            setCreating(false);
            onOpen(id);
          }}
        />
      )}
    </div>
  );
}

export function JoinScreen({ token, onJoined }: { token: string; onJoined: (tripId: string) => void }) {
  const { user } = useAuth();
  const preview = useQuery({
    queryKey: ["invite", token],
    queryFn: () => get<{ tripTitle: string; startDate: string; endDate: string; role: string }>(`/api/invites/${token}`),
    retry: false,
  });
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    if (!user || !preview.data) return;
    post<{ tripId: string }>(`/api/invites/${token}/accept`)
      .then((r) => onJoined(r.tripId))
      .catch((e) => setErr((e as Error).message));
  }, [user, preview.data, token, onJoined]);
  if (preview.isError)
    return (
      <div className="grid min-h-dvh place-items-center p-6 text-center">
        <div>
          <div className="mb-2 text-4xl">🔗</div>
          <div className="font-bold">Приглашение недействительно или истекло</div>
          <div className="mt-1 text-sm text-muted">Попросите друга прислать новую ссылку.</div>
        </div>
      </div>
    );
  if (!user) return <LoginScreen invite={preview.data ?? null} />;
  return (
    <div className="grid min-h-dvh place-items-center p-6 text-center">
      {err ? <div className="text-[var(--danger)]">{err}</div> : <Spinner className="text-[var(--accent)]" />}
    </div>
  );
}

/** Печатная версия плана */
export function PrintView({ tripId }: { tripId: string }) {
  const q = useTripBundle(tripId);
  const d = useDerived(q.data);
  useEffect(() => {
    if (q.data && d) setTimeout(() => window.print(), 600);
  }, [q.data, d]);
  if (!q.data || !d) return <Spinner className="m-10" />;
  const b = q.data;
  return (
    <div className="mx-auto max-w-3xl bg-white p-8 text-black">
      <h1 className="font-display text-2xl font-bold">{b.trip.title}</h1>
      <div className="mb-6 text-sm">{fmtRange(b.trip.startDate, b.trip.endDate)}</div>
      {d.days.map((x) => (
        <div key={x.day} className="mb-4 break-inside-avoid">
          <div className="border-b border-black/20 pb-1 font-bold">
            День {x.index + 1} · {weekday(x.day)}, {fmtDay(x.day)} {x.night ? ` — ночь: ${x.night.title}` : ""}
          </div>
          {x.items.map((it) => {
            const p = it.placeId ? d.places.get(it.placeId) : null;
            const icon = it.kind === "transport" ? MODE[it.mode ?? "other"].emoji : p ? CATEGORY[p.category].emoji : ITEM_KIND[it.kind].emoji;
            return (
              <div key={it.id} className="flex gap-2 py-0.5 text-sm">
                <span className="w-12 shrink-0 tabular-nums">{it.kind === "transport" ? (timeIn(it.departAt, it.fromTz) ?? it.startTime ?? "") : (it.startTime ?? "")}</span>
                <span>{icon}</span>
                <span>
                  <b>{it.title}</b>
                  {p?.nameLocal ? ` (${p.nameLocal})` : ""}
                  {it.bookingRef ? ` · бронь ${it.bookingRef}` : ""}
                  {it.contact ? ` · ${it.contact}` : ""}
                  {it.notes ? <span className="block text-xs opacity-70">{it.notes}</span> : null}
                </span>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
